import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useDeleteClientUserMutation, useGetClientUserQuery } from '../../api/client-users-api';
import { clientUserDeleteModalId, clientUsersAlertId } from '../../constants/constants';
import { selectActiveClientUserId, setActiveClientUserId } from '../../store/client-users-slice';

const ClientUserDeleteModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['client-users', 'common']);
  const dispatch = useDispatch();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();
  const activeClientUserId = useAppSelector(selectActiveClientUserId);
  const [deleteClientUser, { isLoading: isLoadingDeleteClientUser }] = useDeleteClientUserMutation();

  const { data: clientUserData } = useGetClientUserQuery(activeClientUserId || '', {
    skip: !activeClientUserId,
  });

  const closeClientUserDeleteModal = () => {
    dispatch(closeModal(clientUserDeleteModalId));
    dispatch(setActiveClientUserId(null));
  };

  const handleOk = () => {
    if (!activeClientUserId) {
      return;
    }
    resetConfirmationError();

    deleteClientUser(activeClientUserId)
      .unwrap()
      .then(() => {
        dispatch(
          setAlert({
            id: clientUsersAlertId,
            type: 'success',
            title: t('clientUserDeleteModal.messages.successDeleteMessage'),
          }),
        );
        closeClientUserDeleteModal();
      })
      .catch(() => addConfirmationError(t('errors.deleteFailed', { ns: 'common' })));
  };

  return (
    <Modal
      title={t('clientUserDeleteModal.deleteClientUser')}
      onCancel={closeClientUserDeleteModal}
      isLoading={isLoadingDeleteClientUser}
      customSubmitText={t('action.delete', { ns: 'common' })}
      customDeleteText={t('action.cancel', { ns: 'common' })}
      onOk={handleOk}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      {clientUserData?.name && (
        <ModalConfirmationContent
          confirmDeleteText={
            <Trans
              i18nKey="clientUserDeleteModal.confirmationDelete"
              ns="client-users"
              t={t}
              values={{ name: clientUserData.name }}
            />
          }
          confirmationError={confirmationError}
        />
      )}
    </Modal>
  );
};

const ClientUserDeleteModal = () => {
  return <LazyModal modalId={clientUserDeleteModalId} Component={ClientUserDeleteModalContent} />;
};

export default ClientUserDeleteModal;
